import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/client';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check token validity
  const isTokenExpired = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() > exp;
    } catch (e) {
      return true;
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({ username: payload.sub });
          setIsAuthenticated(true);
        } catch (e) {
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [isTokenExpired]);

  // Auto-logout on token expiry
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token)) {
        logout();
        toast.error('Session expired. Please login again.');
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isTokenExpired]);

  const login = async (username, password) => {
    try {
      const response = await authApi.login(username, password);
      localStorage.setItem('token', response.access_token);
      const payload = JSON.parse(atob(response.access_token.split('.')[1]));
      setUser({ username: payload.sub });
      setIsAuthenticated(true);
      toast.success('Welcome back!');
      return true;
    } catch (error) {
      toast.error(error.message || 'Login failed');
      return false;
    }
  };

  const register = async (username, email, password) => {
    try {
      await authApi.register(username, email, password);
      toast.success('Account created! Please login.');
      return true;
    } catch (error) {
      toast.error(error.message || 'Registration failed');
      return false;
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out');
  }, []);

  const forgotPassword = async (email) => {
    try {
      const response = await authApi.forgotPassword(email);
      if (response.debug_token) {
        toast.success(`Reset link generated (dev mode)`);
      } else {
        toast.success('Reset instructions sent to email');
      }
      return response;
    } catch (error) {
      toast.error(error.message || 'Request failed');
      return null;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await authApi.resetPassword(token, newPassword);
      toast.success('Password reset successfully!');
      return true;
    } catch (error) {
      toast.error(error.message || 'Reset failed');
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
